import { useEffect, useState } from "react";
import { Menu, X, Phone, Sparkles } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SITE } from "../data/site";

const NAV = [
  { id: "services", label: "Services", route: "/services" },
  { id: "gallery", label: "Gallery" },
  { id: "process", label: "Process" },
  { id: "about", label: "About" },
  { id: "locations", label: "Locations", route: "/locations" },
  { id: "contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  const goToAnchor = (id) => {
    close();
    if (onHome) {
      const el = document.getElementById(id);
      el?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${id}`);
      // wait a tick for home to mount then scroll
      setTimeout(() => {
        const el = document.getElementById(id);
        el?.scrollIntoView({ behavior: "smooth" });
      }, 60);
    }
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 nav-blur transition-colors duration-300"
    >
      <div className="container-x flex items-center justify-between px-6 md:px-12 lg:px-24 py-4">
        <Link
          to="/"
          data-testid="header-logo"
          className="flex items-center gap-3"
          onClick={close}
        >
          <img
            src={SITE.logo}
            alt="Swift Luxury Walls"
            className="h-10 w-10 rounded-md object-cover"
          />
          <div className="hidden sm:block leading-tight">
            <div className="font-display text-base lg:text-lg text-white tracking-wide">Swift Luxury Walls</div>
            <div className="text-[9px] lg:text-[10px] tracking-[0.22em] uppercase text-[#D4AF37]">
              Premier Custom Entertainment Creations
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) =>
            n.route ? (
              <Link
                key={n.id}
                to={n.route}
                onClick={close}
                data-testid={`nav-link-${n.id}`}
                className="link-gold text-sm tracking-wide"
              >
                {n.label}
              </Link>
            ) : (
              <button
                key={n.id}
                onClick={() => goToAnchor(n.id)}
                data-testid={`nav-link-${n.id}`}
                className="link-gold text-sm tracking-wide bg-transparent"
              >
                {n.label}
              </button>
            )
          )}
          <Link
            to="/design-my-wall"
            data-testid="nav-design-cta"
            className="btn-gold !py-2 !px-4 text-xs tracking-wide"
          >
            <Sparkles className="w-3.5 h-3.5" /> Design My Wall
          </Link>
        </nav>

        <div className="flex items-center gap-3 lg:hidden">
          <Link
            to="/design-my-wall"
            data-testid="nav-design-cta-mobile"
            className="btn-gold !py-2 !px-3 text-[11px]"
            onClick={close}
          >
            <Sparkles className="w-3.5 h-3.5" /> Design
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="p-2 text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden nav-blur border-t border-white/10" data-testid="mobile-menu">
          <div className="container-x px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) =>
              n.route ? (
                <Link
                  key={n.id}
                  to={n.route}
                  onClick={close}
                  data-testid={`mobile-nav-link-${n.id}`}
                  className="text-white text-base py-2 border-b border-white/5"
                >
                  {n.label}
                </Link>
              ) : (
                <button
                  key={n.id}
                  onClick={() => goToAnchor(n.id)}
                  data-testid={`mobile-nav-link-${n.id}`}
                  className="text-white text-base py-2 border-b border-white/5 text-left bg-transparent"
                >
                  {n.label}
                </button>
              )
            )}
            <a
              href={`tel:${SITE.phoneDigits}`}
              data-testid="mobile-call-btn"
              className="btn-gold mt-2"
              onClick={close}
            >
              <Phone className="h-4 w-4" /> Call {SITE.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
